// Scene-builder for the `objects:` array in scene.json. Today the only
// supported `type` is "sphere" (planet bodies + textured magnetosphere
// proxies); extend with new types here as needed.

import * as THREE from 'three';
import { asObj } from './parsers';
import type { SceneJson, SceneObjectSpec } from './parsers';
import type { ViewerContext, RotatingObject } from '../context';
import type { ObjectRegistry } from '../objectRegistry';

interface ProcessSceneDeps {
    rotatingObjects: RotatingObject[];
    registerSceneObject: ObjectRegistry['register'] | undefined;
}

export function processSceneObjects(sceneConf: SceneJson, viewer: ViewerContext, deps: ProcessSceneDeps): void {
    const { rotatingObjects, registerSceneObject } = deps;
    const { scene, renderer } = viewer.three;
    if (!scene) return;
    if (!sceneConf || !Array.isArray(sceneConf['objects'])) return;
    (sceneConf['objects'] as SceneObjectSpec[]).forEach((obj) => {
        if (obj['type'] !== 'sphere') return;
        const radius = (obj['radius'] as number) || 1;
        const color = new THREE.Color((obj['color'] as string) || '#ffffff');
        const geometry = new THREE.SphereGeometry(radius, 48, 48);
        const opacity = (typeof obj['opacity'] === 'number') ? obj['opacity'] : 0.9;
        let material: THREE.MeshBasicMaterial | THREE.MeshPhongMaterial;
        if (obj['unlit'] === true) {
            const tex = (obj['texture'] ? new THREE.TextureLoader().load(obj['texture'] as string) : null);
            material = new THREE.MeshBasicMaterial({ color: color.getHex(), map: tex, transparent: true, opacity });
        } else {
            material = new THREE.MeshPhongMaterial({ color: color.getHex(), shininess: 10, transparent: true, opacity });
            if (obj['texture']) {
                try {
                    const tex = new THREE.TextureLoader().load(obj['texture'] as string);
                    if (renderer && renderer.capabilities && renderer.capabilities.getMaxAnisotropy) {
                        tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
                    }
                    if (typeof THREE.SRGBColorSpace !== 'undefined') {
                        tex.colorSpace = THREE.SRGBColorSpace;
                    }
                    material.map = tex;
                    if (material.color) material.color.set(0xffffff);
                    material.needsUpdate = true;
                } catch { /* swallow */ }
            }
            if (typeof obj['emissive'] === 'string' || typeof obj['emissive'] === 'number') {
                try { material.emissive = new THREE.Color(obj['emissive'] as string | number); } catch { /* swallow */ }
            }
            if (typeof obj['emissiveIntensity'] === 'number') {
                material.emissiveIntensity = obj['emissiveIntensity'] as number;
            } else if (obj['texture'] && (!obj['emissive'])) {
                material.emissive = new THREE.Color(0x111111);
                material.emissiveIntensity = 1.0;
            }
        }
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = (typeof obj['castShadow'] === 'boolean') ? obj['castShadow'] : false;
        mesh.receiveShadow = (typeof obj['receiveShadow'] === 'boolean') ? obj['receiveShadow'] : false;
        const pos = obj['position'] as { x: number; y: number; z: number };
        const physicalPos = new THREE.Vector3(pos.x, pos.y, pos.z);
        mesh.userData['physicalPosition'] = physicalPos.clone();
        if (registerSceneObject) {
            registerSceneObject({ name: (obj['name'] as string) || 'Sphere', type: 'sphere', group: 'physics', threeObject: mesh, position: physicalPos });
        }
        scene.add(mesh);
        const tilt = asObj(obj['tilt']);
        if (tilt && asObj(tilt['axis']) && typeof tilt['degrees'] === 'number') {
            try {
                const axisObj = asObj(tilt['axis'])!;
                const tiltAxis = new THREE.Vector3(
                    (axisObj['x'] as number) || 0,
                    (axisObj['y'] as number) || 0,
                    (axisObj['z'] as number) || 0,
                ).normalize();
                mesh.rotateOnAxis(tiltAxis, (tilt['degrees'] as number) * (Math.PI / 180));
            } catch { /* swallow */ }
        }
        const rotation = asObj(obj['rotation']);
        if (rotation && asObj(rotation['axis']) && typeof rotation['speed'] === 'number') {
            const axisObj = asObj(rotation['axis'])!;
            const axis = new THREE.Vector3(
                (axisObj['x'] as number) || 0,
                (axisObj['y'] as number) || 0,
                (axisObj['z'] as number) || 0,
            ).normalize();
            rotatingObjects.push({ mesh, axis, speedDeg: rotation['speed'] as number });
        }
    });
}
